import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Clock, SignOut, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import InstallAppButton from "@/components/InstallAppButton";

const DEFAULT_BG = "https://static.prod-images.emergentagent.com/jobs/1e15ab80-5652-4b57-ba7e-13d5330de2a9/images/cd5944d2391800ccd404b05edd5fabb8d4b015cb83cddd59b2ae5363d01fe3c6.png";

export default function PendingApproval() {
  const { user, refresh, logout } = useAuth();
  const { branding } = useBranding();

  // Auto-refresh status every 20s while waiting
  useEffect(() => {
    const t = setInterval(() => { refresh?.(); }, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const checkNow = async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.status === "active") {
        toast.success("You've been approved!");
        await refresh?.();
        window.location.href = "/";
      } else {
        toast.info("Still pending. An admin will approve shortly.");
      }
    } catch {
      toast.error("Could not check status");
    }
  };

  return (
    <div data-testid="pending-approval" className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative overflow-hidden">
        <img
          src={branding.login_image_url || DEFAULT_BG}
          alt={branding.firm_name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/80 via-foreground/50 to-transparent" />
        <div className="relative z-10 flex flex-col h-full w-full p-12 text-white">
          <div className="mt-auto">
            <div className="label-tracking !text-white/70 !text-[0.65rem]">Awaiting clearance</div>
            <h1 className="font-display text-5xl xl:text-6xl font-medium leading-[1] tracking-tight mt-3">
              One last <span className="italic font-normal text-white/80">handshake</span>.
            </h1>
            <p className="mt-6 text-sm max-w-md text-white/80 leading-relaxed">
              We're verifying your access with the {branding.firm_name} administrators. You'll be in shortly.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="w-full max-w-md">
          <div className="h-14 w-14 rounded-sm bg-amber-50 flex items-center justify-center mb-6">
            <Clock size={28} weight="fill" className="text-amber-600" />
          </div>
          <div className="label-tracking">Pending approval</div>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-none mt-2 text-slate-900">
            Hold tight, {user?.name?.split(" ")[0] || "Counsel"}.
          </h2>
          <p className="text-sm text-slate-500 mt-4 leading-relaxed">
            Your <span className="font-mono text-slate-900">{user?.email}</span> sign-in needs admin approval.
            An administrator will assign your role and unlock your dashboard. You'll see leads here the moment they do.
          </p>

          <div className="mt-8 border border-slate-200 rounded-sm p-5 bg-white">
            <div className="label-tracking">What happens next?</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li className="flex gap-2">
                <span className="text-slate-300">01</span>
                An admin reviews your account in Settings → Users.
              </li>
              <li className="flex gap-2">
                <span className="text-slate-300">02</span>
                They activate your account and (if needed) promote you to <span className="font-medium">Manager</span> or <span className="font-medium">Admin</span>.
              </li>
              <li className="flex gap-2">
                <span className="text-slate-300">03</span>
                Refresh — or wait, we auto-check every 20 seconds.
              </li>
            </ul>
          </div>

          <div className="mt-6 flex gap-3">
            <Button
              data-testid="pending-check-btn"
              onClick={checkNow}
              className="flex-1 h-11 rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
            >
              <ArrowsClockwise size={14} /> Check status
            </Button>
            <Button
              data-testid="pending-logout-btn"
              onClick={logout}
              variant="outline"
              className="h-11 rounded-sm gap-2"
            >
              <SignOut size={14} /> Sign out
            </Button>
          </div>
          <div className="mt-6 max-w-xs mx-auto">
            <InstallAppButton />
          </div>
        </div>
      </div>
    </div>
  );
}
